import React, { useState, useCallback } from 'react';
import { Box, Button, TextField, Typography, CircularProgress } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { selectApprovalLoading } from '../../store/selectors/translationsSelectors';
import { approveTranslationAndRefresh, declineTranslationAndRefresh } from '../../store/slices/translationsSlice';
import { closeTranslationDetails } from '../../store/slices/uiSlice';

interface ApproveDeclineTranslationProps {
  translationRecordId: string;
  language: string;
}

const ApproveDeclineTranslation: React.FC<ApproveDeclineTranslationProps> = ({
  translationRecordId,
  language,
}) => {

  const [disableSubmit, setDisableSubmit] = useState(true);
  const [declineReason, setDeclineReason] = useState('');
  const [showApproveDeclineBox, setShowApproveDeclineBox] = useState(false);

  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectApprovalLoading);
  
  const resetForm = useCallback(() => {
    setShowApproveDeclineBox(false);
    setDeclineReason('');
    setDisableSubmit(true);
  }, []);       

  const onDeclineReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDeclineReason(value);
    setDisableSubmit(value.trim().length === 0);
  };

  const toggleDeclineBox = useCallback(() => {
    setShowApproveDeclineBox(prev => {
      if (prev) {
        resetForm();
      }
      return !prev;
    });
  }, [resetForm]);
        
  const handleApprove = async () => {
    if (loading) return;
    const postData = {
      translationRecordId,
      language,
    };
    const result = await dispatch(approveTranslationAndRefresh(postData)).unwrap();
    if (result.status === 'Success') {
      dispatch(closeTranslationDetails());
    }
  };

  const handleDecline = async () => {
    if (loading) return;
    const postData = {
      translationRecordId,
      language,
      declineReason: declineReason.trim(),
    };
    const result = await dispatch(declineTranslationAndRefresh(postData)).unwrap();
    if (result.status === 'Success') {
      resetForm();
    }
  };
        
  return (
    <Box>
      {!showApproveDeclineBox ? (
        <Box display="flex" gap={2} alignItems="center" justifyContent="space-between">
          <Button 
            variant="contained" 
            size="medium" 
            onClick={handleApprove} 
            disabled={loading}
            sx={styles.button}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {loading ? 'APPROVING...' : 'APPROVE'}
          </Button>
          <Button variant="outlined" size="medium" onClick={toggleDeclineBox} disabled={loading} sx={styles.button}>
            DECLINE
          </Button>
        </Box>
      ) : (
        <Box sx={styles.declineBox}>
          <TextField 
            fullWidth 
            variant="outlined" 
            placeholder="*Decline reason" 
            value={declineReason} 
            onChange={onDeclineReasonChange} 
            size="small" 
          />
          <Typography variant="caption" color="text.secondary" component="div">
            Please give a reason for your decline
          </Typography>
          <Box display="flex" gap={2} alignItems="center" justifyContent="space-between" sx={styles.buttonContainer}>
            <Button variant="contained" disabled={disableSubmit || loading} size="medium" onClick={handleDecline} sx={styles.button} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}>
              {loading ? 'SUBMITTING...' : 'Submit Decline'}
            </Button>
            <Button variant="outlined" size="medium" onClick={toggleDeclineBox} sx={styles.button} disabled={loading}>
              CANCEL
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const styles = {
  button: { flex: 1 },
  declineBox: { flex: 1, mt: 2 },
  buttonContainer: { flex: 1, mt: 2 },
};

export default ApproveDeclineTranslation;